import { type ReactNode, useCallback, useEffect, useState } from "react"

import {
  confirmMfaSetup,
  disableMfa,
  mfaSetup,
  mfaStatus,
  reissueRecoveryCodes,
} from "../../api/mfa.api"
import { isApiProblem } from "../../api/problem"
import { Badge, Button, QrCode, TextField } from "../../ui"
import {
  codeError,
  mfaView,
  recoveryCodesFileName,
  recoveryCodesText,
  secretGroups,
} from "./mfa-model"
import { TEXT_OFFLINE } from "./profile-model"
import styles from "./profile.module.css"
import type { CabinetMfaSecret, CabinetMfaStatus, MfaMethod } from "@planerius/contracts/cabinet-auth"

/**
 * Блок «Второй фактор» (1-Э1.10, 5-М7).
 *
 * ЧЕТЫРЕ СОСТОЯНИЯ И НИ ОДНОГО ЛИШНЕГО: смотрим (что включено и сколько кодов), заводим
 * (QR и секрет), подтверждаем кодом, показываем резервные коды. Выключение и перевыпуск —
 * это то же подтверждение кодом, только с другим действием на конце.
 *
 * КОДЫ ПОКАЗЫВАЮТСЯ ОДИН РАЗ, И ЭТО НЕ РЕШЕНИЕ ЭКРАНА. В базе лежат только их хеши,
 * отдать список повторно сервер физически не может. Поэтому пока коды на экране, уйти
 * с него нельзя случайно: список закрывается только кнопкой «Я сохранил коды».
 *
 * СЕКРЕТ И КОДЫ НЕ ПОПАДАЮТ НИ В localStorage, НИ В АДРЕС, ни в журнал браузера — только
 * в состояние вкладки, которое умирает вместе с ней.
 */

type Stage =
  | { readonly kind: "idle" }
  | { readonly kind: "secret"; readonly secret: CabinetMfaSecret }
  | { readonly kind: "codes"; readonly codes: readonly string[]; readonly reissued: boolean }
  /**
   * Начатая настройка: QR уже показан, остался код.
   *
   * ОТДЕЛЬНОЕ СОСТОЯНИЕ, А НЕ НОВЫЙ СЕКРЕТ. «Продолжить настройку» раньше выпускала
   * новый секрет — и гасила тот, который человек уже отсканировал: он вводил код
   * из приложения, которое к этому моменту перестало подходить, и читал «код не подошёл»,
   * сделав всё правильно. Секрет второй раз не показывает никто (в базе шифротекст),
   * поэтому продолжение — это ввод кода, а не картинка.
   */
  | { readonly kind: "pending" }
  | { readonly kind: "ask"; readonly action: "disable" | "reissue" | "reenroll" }

export function MfaBlock(props: { readonly onChanged?: () => void }): ReactNode {
  const [status, setStatus] = useState<CabinetMfaStatus | null>(null)
  const [stage, setStage] = useState<Stage>({ kind: "idle" })
  const [code, setCode] = useState("")
  const [method, setMethod] = useState<MfaMethod>("totp")
  const [showError, setShowError] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async (signal?: AbortSignal): Promise<void> => {
    try {
      setStatus(await mfaStatus(signal))
      setProblem(null)
    } catch (error) {
      if (signal?.aborted === true) return
      if (!isApiProblem(error)) throw error
      setProblem(error.detail ?? error.title)
    }
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    void load(controller.signal)
    return () => {
      controller.abort()
    }
  }, [load])

  const reset = (): void => {
    setCode("")
    setShowError(false)
    setProblem(null)
    setMethod("totp")
  }

  const run = async (action: () => Promise<void>): Promise<void> => {
    setBusy(true)
    setProblem(null)
    try {
      await action()
    } catch (error) {
      if (!isApiProblem(error)) throw error
      setProblem(error.detail ?? error.title)
    } finally {
      setBusy(false)
    }
  }

  /**
   * Выпустить секрет.
   *
   * proof — код действующего фактора. Сервер требует его, когда фактор включён:
   * подтверждение нового секрета гасит прежний вместе со всеми резервными кодами,
   * то есть меняет замок, и одной открытой вкладки для этого мало.
   */
  const begin = (proof?: { readonly code: string; readonly method: MfaMethod }): Promise<void> =>
    run(async () => {
      const secret = await mfaSetup(proof)
      reset()
      setStage({ kind: "secret", secret })
    })

  const confirm = (): Promise<void> =>
    run(async () => {
      const check = codeError(code, "totp")
      setShowError(true)
      if (check !== null) return
      const result = await confirmMfaSetup(code)
      reset()
      setStage({ kind: "codes", codes: result.recoveryCodes, reissued: false })
      await load()
      props.onChanged?.()
    })

  const finish = (action: "disable" | "reissue" | "reenroll"): Promise<void> =>
    run(async () => {
      const check = codeError(code, method)
      setShowError(true)
      if (check !== null) return
      if (action === "reenroll") {
        // Код действующего фактора уходит вместе с запросом секрета: сервер проверит
        // его и только тогда выпустит новый. Экран после этого — обычный QR.
        await begin({ code, method })
        return
      }
      if (action === "disable") {
        await disableMfa(code, method)
        reset()
        setStage({ kind: "idle" })
      } else {
        const result = await reissueRecoveryCodes(code, method)
        reset()
        setStage({ kind: "codes", codes: result.recoveryCodes, reissued: true })
      }
      await load()
      props.onChanged?.()
    })

  if (status === null) {
    return (
      <section className={styles.block}>
        <div className={styles.blockHead}>
          <span className={styles.blockTitle}>Второй фактор</span>
        </div>
        {problem === null ? (
          <p className={styles.muted}>Загружаем…</p>
        ) : (
          <div className={styles.row}>
            <Button size="sm" onClick={() => void load()}>
              Повторить
            </Button>
          </div>
        )}
      </section>
    )
  }

  const view = mfaView(status)
  const online = typeof navigator === "undefined" ? true : navigator.onLine
  const offline = online ? undefined : TEXT_OFFLINE
  const validation = showError ? codeError(code, stage.kind === "secret" ? "totp" : method) : null

  return (
    <section className={styles.block}>
      <div className={styles.blockHead}>
        <span className={styles.blockTitle}>Второй фактор</span>
        <Badge tone={view.badgeTone}>{view.badgeText}</Badge>
      </div>
      <span className={styles.muted}>{view.explain}</span>

      {stage.kind === "secret" ? (
        <div className={styles.form}>
          <p className={styles.hint}>
            Откройте приложение-аутентификатор (Яндекс.Ключ, Google Authenticator, 1Password)
            и наведите камеру на этот код. Если камеры нет — введите ключ руками.
          </p>
          <QrCode text={stage.secret.otpauthUri} label="Код для приложения-аутентификатора" />
          <p className={styles.secret}>
            {secretGroups(stage.secret.secret).map((group) => (
              <span key={group} className={styles.secretGroup}>
                {group}
              </span>
            ))}
          </p>
          <TextField
            label="Код из приложения"
            hint="Шесть цифр. Они меняются каждые 30 секунд — введите текущие."
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            error={validation ?? undefined}
            onChange={(event) => setCode(event.target.value)}
          />
          {problem === null ? null : (
            <div className={styles.error} role="alert">
              {problem}
            </div>
          )}
          <div className={styles.row}>
            <Button
              onClick={() => {
                reset()
                setStage({ kind: "idle" })
              }}
            >
              Отмена
            </Button>
            <Button
              variant="primary"
              loading={busy}
              loadingLabel="Проверяем…"
              deniedReason={offline}
              onClick={() => void confirm()}
            >
              Подтвердить
            </Button>
          </div>
        </div>
      ) : null}

      {stage.kind === "codes" ? (
        <div className={styles.form}>
          <div className={styles.notice}>
            {stage.reissued
              ? "Новые резервные коды готовы, прежние больше не работают."
              : "Второй фактор включён. Вот резервные коды — больше мы их не покажем."}
          </div>
          <p className={styles.hint}>
            Каждый код срабатывает ОДИН раз. Храните их не там, где телефон с приложением:
            потеряв телефон, вы войдёте только по такому коду.
          </p>
          <ul className={styles.codes}>
            {stage.codes.map((item) => (
              <li key={item} className={styles.codeItem}>
                {item}
              </li>
            ))}
          </ul>
          <div className={styles.row}>
            <Button size="sm" onClick={() => void copyCodes(stage.codes)}>
              Скопировать
            </Button>
            <Button size="sm" onClick={() => downloadCodes(stage.codes)}>
              Скачать файлом
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setStage({ kind: "idle" })
              }}
            >
              Я сохранил коды
            </Button>
          </div>
        </div>
      ) : null}

      {stage.kind === "pending" ? (
        <div className={styles.form}>
          <div className={styles.notice}>
            Настройка начата: код уже показан приложению. Введите шесть цифр из него.
          </div>
          <p className={styles.hint}>
            Если вы не успели отсканировать код или удалили его из приложения — начните
            заново: прежний ключ перестанет подходить.
          </p>
          <TextField
            label="Код из приложения"
            hint="Шесть цифр. Они меняются каждые 30 секунд — введите текущие."
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            error={showError ? (codeError(code, "totp") ?? undefined) : undefined}
            onChange={(event) => setCode(event.target.value)}
          />
          {problem === null ? null : (
            <div className={styles.error} role="alert">
              {problem}
            </div>
          )}
          <div className={styles.row}>
            <Button
              onClick={() => {
                reset()
                setStage({ kind: "idle" })
              }}
            >
              Отмена
            </Button>
            <Button loading={busy} deniedReason={offline} onClick={() => void begin()}>
              Начать заново
            </Button>
            <Button
              variant="primary"
              loading={busy}
              loadingLabel="Проверяем…"
              deniedReason={offline}
              onClick={() => void confirm()}
            >
              Подтвердить
            </Button>
          </div>
        </div>
      ) : null}

      {stage.kind === "ask" ? (
        <div className={styles.form}>
          <p className={styles.hint}>
            {stage.action === "disable"
              ? "Чтобы выключить фактор, подтвердите, что он у вас есть: введите код из приложения или резервный код."
              : stage.action === "reenroll"
                ? "Подключение другого приложения гасит прежнее и все резервные коды. Подтвердите, что текущий фактор у вас: введите код из приложения или резервный код."
                : "Новые коды заменят прежние. Подтвердите кодом из приложения или резервным кодом."}
          </p>
          <TextField
            label={method === "totp" ? "Код из приложения" : "Резервный код"}
            value={code}
            error={validation ?? undefined}
            onChange={(event) => setCode(event.target.value)}
          />
          <label className={styles.hint}>
            <input
              type="checkbox"
              checked={method === "recovery"}
              onChange={(event) => {
                setMethod(event.target.checked ? "recovery" : "totp")
                setShowError(false)
              }}
            />{" "}
            У меня нет телефона — введу резервный код
          </label>
          {problem === null ? null : (
            <div className={styles.error} role="alert">
              {problem}
            </div>
          )}
          <div className={styles.row}>
            <Button
              onClick={() => {
                reset()
                setStage({ kind: "idle" })
              }}
            >
              Отмена
            </Button>
            <Button
              variant={stage.action === "disable" ? "danger" : "primary"}
              loading={busy}
              loadingLabel="Проверяем…"
              deniedReason={offline}
              onClick={() => void finish(stage.action)}
            >
              {stage.action === "disable"
                ? "Выключить фактор"
                : stage.action === "reenroll"
                  ? "Показать новый код"
                  : "Перевыпустить коды"}
            </Button>
          </div>
        </div>
      ) : null}

      {stage.kind === "idle" ? (
        <>
          {view.recoveryLine === null ? null : (
            <p className={view.recoveryLow ? styles.warnLine : styles.hint}>{view.recoveryLine}</p>
          )}
          {problem === null ? null : (
            <div className={styles.error} role="alert">
              {problem}
            </div>
          )}
          <div className={styles.row}>
            <Button
              loading={busy}
              deniedReason={offline}
              onClick={() => {
                reset()
                // Три разных пути, и путать их нельзя: продолжить начатое (ввод кода),
                // перезавести при включённом факторе (сначала код текущего) и завести
                // с нуля (сразу новый секрет).
                if (view.pending) {
                  setStage({ kind: "pending" })
                } else if (status.enabled) {
                  setStage({ kind: "ask", action: "reenroll" })
                } else {
                  void begin()
                }
              }}
            >
              {view.enableLabel}
            </Button>
            <Button
              size="sm"
              deniedReason={offline ?? (status.enabled ? undefined : "Сначала включите фактор")}
              onClick={() => {
                reset()
                setStage({ kind: "ask", action: "reissue" })
              }}
            >
              Резервные коды
            </Button>
            {status.enabled ? (
              <Button
                size="sm"
                deniedReason={offline ?? view.disableDenied}
                onClick={() => {
                  reset()
                  setStage({ kind: "ask", action: "disable" })
                }}
              >
                Выключить
              </Button>
            ) : null}
          </div>
        </>
      ) : null}
    </section>
  )
}

/**
 * «Скопировать». Тихий отказ, если буфер недоступен: коды и так на экране, и ронять
 * экран из-за буфера обмена незачем.
 */
async function copyCodes(codes: readonly string[]): Promise<void> {
  try {
    await navigator.clipboard.writeText(recoveryCodesText(codes, new Date()))
  } catch {
    // Браузер без разрешения на буфер — человек перепишет с экрана.
  }
}

/** «Скачать файлом»: обычный текст, который откроется на любом устройстве. */
function downloadCodes(codes: readonly string[]): void {
  const now = new Date()
  const blob = new Blob([recoveryCodesText(codes, now)], { type: "text/plain;charset=utf-8" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = recoveryCodesFileName(now)
  link.click()
  URL.revokeObjectURL(url)
}
